import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import styles from './CSVUploadPage.module.css';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

// Session storage key for recent uploads
const RECENT_UPLOADS_KEY = 'csv_recent_uploads';

// Get recent uploads from sessionStorage
const getRecentUploads = (slug) => {
  try {
    const stored = sessionStorage.getItem(RECENT_UPLOADS_KEY);
    if (!stored) return [];
    const uploads = JSON.parse(stored);
    // Filter by slug and return most recent first
    return uploads
      .filter(u => u.slug === slug)
      .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
  } catch {
    return [];
  }
};

// Save upload to sessionStorage
const saveRecentUpload = (slug, uploadData) => {
  try {
    const stored = sessionStorage.getItem(RECENT_UPLOADS_KEY);
    const uploads = stored ? JSON.parse(stored) : [];

    // Add new upload with timestamp
    uploads.push({
      slug,
      ...uploadData,
      uploadedAt: new Date().toISOString()
    });

    // Keep only last 10 uploads per session
    const trimmed = uploads.slice(-50);
    sessionStorage.setItem(RECENT_UPLOADS_KEY, JSON.stringify(trimmed));
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

// Format timestamp for display
const formatUploadTime = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const CSVUploadPage = () => {
  const { slug } = useParams();
  const fileInputRef = useRef(null);

  // Page state
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auth state
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Recent uploads state
  const [recentUploads, setRecentUploads] = useState([]);

  // Load page info on mount
  useEffect(() => {
    const loadPageInfo = async () => {
      try {
        const response = await fetch(`/api/csv/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Page not found');
          }
          throw new Error('Failed to load page');
        }
        const data = await response.json();
        setPageInfo(data.page);

        // Load recent uploads from session storage
        setRecentUploads(getRecentUploads(slug));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPageInfo();
  }, [slug]);

  // Handle password verification
  const handleVerify = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setVerifying(true);

    try {
      const response = await fetch(`/api/csv/${slug}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Invalid password');
      }

      const data = await response.json();
      setToken(data.token);
      setAuthenticated(true);
      setPassword(''); // Clear password from state
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      validateAndSetFile(droppedFiles[0]);
    }
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  // Validate file
  const validateAndSetFile = (selectedFile) => {
    setUploadError(null);
    setUploadSuccess(null);

    // Check file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', '.csv'];
    const isValidType = validTypes.some(type =>
      selectedFile.type === type || selectedFile.name.endsWith('.csv')
    );

    if (!isValidType) {
      setUploadError('Please select a CSV file');
      return;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setUploadError('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file || !token) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      });

      // Handle completion
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.message || 'Upload failed'));
            } catch {
              reject(new Error('Upload failed'));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
      });

      xhr.open('POST', `/api/csv/${slug}/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);

      const result = await uploadPromise;
      setUploadSuccess(result.upload);

      // Save to session storage for recent uploads list
      saveRecentUpload(slug, {
        id: result.upload.id,
        fileName: result.upload.fileName,
        fileSize: result.upload.fileSize,
        rowCount: result.upload.rowCount,
        columnCount: result.upload.columnHeaders?.length
      });

      // Update recent uploads state
      setRecentUploads(getRecentUploads(slug));

      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Clear file selection
  const handleClearFile = () => {
    setFile(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Reset for new upload
  const handleUploadAnother = () => {
    setUploadSuccess(null);
    setFile(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <LoadingSpinner />
            <p>Loading page...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.errorState}>
            <div className={styles.errorIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1>Page Not Found</h1>
            <p>The upload page you're looking for doesn't exist or is no longer active.</p>
            <Link to="/" className={styles.homeLink}>Go to Homepage</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Logo */}
        <div className={styles.logo}>
          <img loading="lazy" src="/oval-logo.webp" alt="OVAL" />
        </div>

        {/* Page Header */}
        <div className={styles.header}>
          <h1>{pageInfo.name}</h1>
          {pageInfo.description && (
            <p className={styles.description}>{pageInfo.description}</p>
          )}
          {pageInfo.agency && (
            <span className={styles.agencyBadge}>{pageInfo.agency.name}</span>
          )}
        </div>

        {/* Password Form */}
        {!authenticated && (
          <div className={styles.card}>
            <h2>Enter Password to Continue</h2>
            <p className={styles.cardDescription}>
              Please enter the password provided to you to access this upload page.
            </p>

            <form onSubmit={handleVerify} className={styles.passwordForm}>
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={verifying}
                autoFocus
              />
              {authError && (
                <div className={styles.authError}>{authError}</div>
              )}
              <Button type="submit" disabled={!password || verifying} fullWidth>
                {verifying ? 'Verifying...' : 'Continue'}
              </Button>
            </form>
          </div>
        )}

        {/* Upload Area */}
        {authenticated && !uploadSuccess && (
          <div className={styles.card}>
            <h2>Upload CSV File</h2>
            <p className={styles.cardDescription}>
              Select or drag a CSV file to upload. Maximum file size is 10MB.
            </p>

            {/* Drop Zone */}
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dragging : ''} ${file ? styles.hasFile : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !file && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                onChange={handleFileSelect}
                className={styles.fileInput}
              />

              {!file ? (
                <>
                  <div className={styles.dropIcon}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <p className={styles.dropText}>
                    <strong>Click to select</strong> or drag and drop
                  </p>
                  <p className={styles.dropHint}>CSV files only, max 10MB</p>
                </>
              ) : (
                <div className={styles.selectedFile}>
                  <div className={styles.fileIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div className={styles.fileInfo}>
                    <p className={styles.fileName}>{file.name}</p>
                    <p className={styles.fileSize}>{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    className={styles.clearFileBtn}
                    onClick={(e) => { e.stopPropagation(); handleClearFile(); }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className={styles.progressWrapper}>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className={styles.progressText}>{uploadProgress}%</span>
              </div>
            )}

            {/* Upload Error */}
            {uploadError && (
              <div className={styles.uploadError}>{uploadError}</div>
            )}

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </div>
        )}

        {/* Success State */}
        {uploadSuccess && (
          <div className={styles.card}>
            <div className={styles.successState}>
              <div className={styles.successIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9 12 12 15 16 10" />
                </svg>
              </div>
              <h2>Upload Successful!</h2>
              <p>Your file has been uploaded and processed.</p>

              <div className={styles.uploadDetails}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Filename</span>
                  <span className={styles.detailValue}>{uploadSuccess.fileName}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Size</span>
                  <span className={styles.detailValue}>{formatFileSize(uploadSuccess.fileSize)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Rows</span>
                  <span className={styles.detailValue}>{uploadSuccess.rowCount?.toLocaleString()}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Columns</span>
                  <span className={styles.detailValue}>{uploadSuccess.columnHeaders?.length}</span>
                </div>
              </div>

              <div className={styles.successActions}>
                <Link
                  to={`/data/${slug}/${uploadSuccess.id}`}
                  className={styles.viewDataLink}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  View Uploaded Data
                </Link>
                <Button onClick={handleUploadAnother} fullWidth>
                  Upload Another File
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Recent Uploads */}
        {authenticated && recentUploads.length > 0 && (
          <div className={styles.card}>
            <h2>Recent Uploads</h2>
            <p className={styles.cardDescription}>
              Uploads from this session
            </p>
            <div className={styles.recentList}>
              {recentUploads.slice(0, 5).map((upload) => (
                <div key={upload.id} className={styles.recentItem}>
                  <div className={styles.recentIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div className={styles.recentInfo}>
                    <p className={styles.recentFileName}>{upload.fileName}</p>
                    <p className={styles.recentMeta}>
                      {upload.rowCount?.toLocaleString()} rows • {formatUploadTime(upload.uploadedAt)}
                    </p>
                  </div>
                  <Link
                    to={`/data/${slug}/${upload.id}`}
                    className={styles.recentViewLink}
                    title="View data"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <p>Powered by <a href="/">OVAL</a></p>
        </div>
      </div>
    </div>
  );
};

export default CSVUploadPage;
